// Sidebar.tsx
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";

export interface SidebarItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
  roles?: string[];        // restrict visibility by role
  adminOnly?: boolean;     // visible only for admin
  children?: SidebarItem[]; // nested items
}

export interface SidebarProps {
  items: SidebarItem[];
  userRole: string;
  isAdmin: boolean;        // admin flag
  isOpen?: boolean;
  toggle?: () => void;
  className?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  items,
  userRole,
  isAdmin,
  isOpen = true,
  toggle,
  className = ""
}) => {
  const location = useLocation();

  // Filter items recursively
  const filterItems = (items: SidebarItem[]): SidebarItem[] => {
    return items
      .filter((item) => {
        // hide adminOnly items for non-admins
        if (item.adminOnly && !isAdmin) return false;

        // hide if userRole is not allowed
        if (item.roles && !item.roles.includes(userRole)) return false;

        return true;
      })
      .map((item) => ({
        ...item,
        children: item.children ? filterItems(item.children) : undefined
      }));
  };

  const filteredItems = filterItems(items);

  const renderItem = (item: SidebarItem, depth: number = 0) => {
    const isActive = location.pathname === item.path;

    return (
      <div key={item.path} className={`ml-${depth * 4}`}>
        <Link to={item.path}>
          <motion.div
            whileHover={{ scale: 1.05 }}
            className={`flex items-center gap-3 p-3 mx-2 rounded-md cursor-pointer ${
              isActive
                ? "bg-green-700 text-yellow-300 shadow-lg"
                : "hover:bg-green-800/50"
            }`}
          >
            {item.icon && <span className="text-lg">{item.icon}</span>}
            {isOpen && <span>{item.label}</span>}
          </motion.div>
        </Link>

        {item.children && isOpen && (
          <div className="ml-4">
            {item.children.map((child) => renderItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <motion.div
      animate={{ width: isOpen ? 130 : 0 }}
      className={`fixed top-0 left-0 h-full bg-black/90 text-white shadow-lg flex flex-col z-50 overflow-hidden ${className}`}
      transition={{ duration: 0.3 }}
    >
      {toggle && (
        <button
          onClick={toggle}
          className="p-3 m-2 rounded-md hover:bg-green-800 transition text-white"
        >
          {isOpen ? "←" : "→"}
        </button>
      )}

      <div className="flex-1 flex flex-col gap-1 mt-4">
        {filteredItems.length > 0 ? (
          filteredItems.map((item) => renderItem(item))
        ) : (
          <p className="text-gray-400 text-center mt-4 text-sm">
            No menu items available
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default Sidebar;
